// 기업 로고 URL — 딥서치 CDN(cdn.deepsearch.com)에서 종목 심볼로 바로 받는다.
//
// 인증이 없고 CORS 가 열려 있어(access-control-allow-origin: *) <img src> 로 그냥 쓴다.
// 규격은 240x240 PNG, 국내 로고는 투명 배경이다.
//
// ⚠️ 없는 로고는 404 가 아니라 **403(S3 AccessDenied XML)** 로 온다. 그래서
//    "먼저 fetch 해서 있는지 본다"가 성립하지 않고, <img> 의 onError 폴백이 유일한
//    방법이다(CompanyLogo.tsx). 여기서는 '주소를 만들 수 있는가'만 판단한다.
//
// 심볼 포맷이 화면마다 다르다 — 그래서 이 함수가 필요하다:
//   WaccTab 유니버스   "KRX:005930" · "AAPL"
//   BuffettPage       "NASDAQ:AAPL" · "NYSE:KO"
//   PeerPage peers    stock_code "005930" · ticker "AAPL"

const CDN = "https://cdn.deepsearch.com/company";

/** 국내 6자리 종목코드 — 끝자리가 영문인 신형 우선주도 있다(00104K = 한화3우B) */
const KR_CODE = /^\d{5}[0-9A-Za-z]$/;
/** 해외 티커 — 영문으로 시작하는 1~6자. 점은 하이픈으로 바뀐다(BRK.B → BRK-B) */
const GLOBAL_TICKER = /^[A-Za-z][A-Za-z.-]{0,5}$/;

export type LogoRef =
  | { market: "kr"; code: string }
  | { market: "global"; code: string };

/**
 * 심볼에서 거래소 접두사를 떼고 시장을 판별한다.
 * 형태가 둘 중 어느 쪽도 아니면 null — 로고를 시도조차 하지 않는다.
 */
export function parseSymbol(symbol: string | null | undefined): LogoRef | null {
  const raw = String(symbol ?? "").trim();
  if (!raw) return null;
  // "KRX:005930" · "NASDAQ:AAPL" → 뒤쪽만 쓴다
  const core = raw.replace(/^[A-Za-z]+:/, "").trim();
  if (!core) return null;
  if (KR_CODE.test(core)) return { market: "kr", code: core.toUpperCase() };
  if (GLOBAL_TICKER.test(core)) return { market: "global", code: core.toUpperCase().replace(/\./g, "-") };
  return null;
}

/** 로고 주소. 만들 수 없는 심볼이면 null (호출부는 폴백을 그린다) */
export function logoUrl(symbol: string | null | undefined): string | null {
  const ref = parseSymbol(symbol);
  if (!ref) return null;
  return ref.market === "kr" ? `${CDN}/logo/${ref.code}.png` : `${CDN}/global-logo/${ref.code}.png`;
}

/**
 * 403 을 한 번 받은 주소 — 세션 동안 다시 시도하지 않는다.
 *
 * React 원자(ui/CompanyLogo)와 바닐라 차트 위젯(lib/vachart)이 **같은 캐시를 쓴다.**
 * 각자 들고 있으면 같은 기업을 두 곳에서 그릴 때 403 을 두 번 받는다.
 */
const FAILED = new Set<string>();

export function markLogoFailed(url: string): void {
  FAILED.add(url);
}

export function logoFailed(url: string): boolean {
  return FAILED.has(url);
}

/**
 * 폴백 배지에 쓸 한 글자. 기업명이 먼저고, 없으면 심볼에서 딴다.
 * 국내 코드는 숫자라 글자로서 의미가 없으니 이름이 없으면 빈 문자열로 둔다.
 */
export function logoInitial(name?: string | null, symbol?: string | null): string {
  const n = String(name ?? "").trim();
  if (n) return n[0].toUpperCase();
  const ref = parseSymbol(symbol);
  if (ref?.market === "global") return ref.code[0];
  return "";
}
